import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NotificationData } from '../../../services/notification.service';
import {
  ModalStateService,
  NotificationModalOpenBehavior,
} from '../../../services/modal-state.service';
import {
  DIALOG_STACK_EXCEPTIONS,
  DialogMutexService,
} from '../../../services/dialog-mutex.service';
import { LoggerService } from '../../../services/logger.service';

export interface WayPurchaseModalState {
  isOpen: boolean;
  notification: NotificationData | null;
  shouldStore?: boolean;
}

export interface OpenWayPurchaseModalOptions {
  notification: NotificationData;
  behavior?: NotificationModalOpenBehavior;
  shouldStore?: boolean;
  clearDismissSuppression?: boolean;
  /**
   * Explicit stack exception: keep the notification modal open underneath
   * (order-request Way lookup overlay). See {@link DIALOG_STACK_EXCEPTIONS.WAY_ON_NOTIFICATION}.
   */
  preserveUnderlyingModal?: boolean;
}

@Injectable({ providedIn: 'root' })
export class WayPurchaseModalStateService {
  private readonly modalStateSubject = new BehaviorSubject<WayPurchaseModalState>({
    isOpen: false,
    notification: null,
    shouldStore: true,
  });

  readonly modalState$ = this.modalStateSubject.asObservable();

  private notificationQueue: { notification: NotificationData; shouldStore: boolean }[] = [];
  private isClosing = false;
  private closingTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly dismissedEntryKeys = new Map<string, number>();
  private readonly DISMISSED_ENTRY_TTL_MS = 5 * 60 * 1000;

  constructor(
    private readonly logger: LoggerService,
    private readonly modalStateService: ModalStateService,
    private readonly dialogMutex: DialogMutexService
  ) {
    // Close UI when NotificationModal (or another owner) force-acquires and preempts Way.
    this.dialogMutex.releases$.subscribe(owner => {
      if (owner !== 'way-purchase-modal' || !this.modalStateSubject.value.isOpen) {
        return;
      }
      this.isClosing = false;
      if (this.closingTimeout) {
        clearTimeout(this.closingTimeout);
        this.closingTimeout = null;
      }
      this.notificationQueue = [];
      this.modalStateSubject.next({
        isOpen: false,
        notification: null,
        shouldStore: true,
      });
    });
  }

  getCurrentState(): WayPurchaseModalState {
    return this.modalStateSubject.value;
  }

  openModal(options: OpenWayPurchaseModalOptions): void {
    const {
      notification,
      behavior = 'respect_state',
      shouldStore = true,
      clearDismissSuppression = behavior === 'replace',
      preserveUnderlyingModal = false,
    } = options;

    if (preserveUnderlyingModal) {
      if (
        !this.dialogMutex.tryAcquire('way-purchase-modal', {
          exception: DIALOG_STACK_EXCEPTIONS.WAY_ON_NOTIFICATION,
        })
      ) {
        this.logger.debug(
          'Way purchase modal blocked by dialog mutex (preserveUnderlying exception denied).'
        );
        if (shouldStore !== false) {
          this.modalStateService.storeNotificationInLocalStorage(notification);
        }
        return;
      }
      if (behavior === 'replace') {
        this.openReplacing(notification, shouldStore, clearDismissSuppression);
        return;
      }
      if (!this.openRespectingState(notification, shouldStore)) {
        this.dialogMutex.release('way-purchase-modal');
      }
      return;
    }

    if (behavior === 'replace') {
      if (this.modalStateService.getCurrentState().isOpen) {
        this.modalStateService.hideModal();
      }
      this.dialogMutex.forceAcquire('way-purchase-modal');
      this.openReplacing(notification, shouldStore, clearDismissSuppression);
      return;
    }

    // respect_state: mutex discard — do not stack on or queue behind an open NotificationModal.
    if (
      this.modalStateService.getCurrentState().isOpen ||
      this.dialogMutex.hasOwner('notification-modal')
    ) {
      this.logger.debug(
        'Way purchase modal blocked by open NotificationModal; storing notification.'
      );
      if (shouldStore !== false) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      return;
    }

    if (!this.dialogMutex.tryAcquire('way-purchase-modal')) {
      this.logger.debug('Way purchase modal blocked by dialog mutex; storing notification.');
      if (shouldStore !== false) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      return;
    }

    if (!this.openRespectingState(notification, shouldStore)) {
      this.dialogMutex.release('way-purchase-modal');
    }
  }

  hideModal(): void {
    this.isClosing = true;

    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }

    const currentNotification = this.modalStateSubject.value.notification;
    const entryId = this.getLprEntryId(currentNotification);
    if (entryId) {
      this.dismissedEntryKeys.set(`entry:${entryId}`, Date.now());
      this.pruneDismissedEntryKeys();
    }

    this.modalStateSubject.next({
      isOpen: false,
      notification: null,
      shouldStore: true,
    });

    // Keep mutex until after drain so deferred notification opens cannot race the Way queue.
    this.closingTimeout = setTimeout(() => {
      this.isClosing = false;

      if (this.notificationQueue.length > 0) {
        const next = this.notificationQueue.shift();
        this.notificationQueue = [];
        if (next) {
          const nextEntryId = this.getLprEntryId(next.notification);
          const dismissedAt = nextEntryId
            ? this.dismissedEntryKeys.get(`entry:${nextEntryId}`)
            : undefined;
          if (dismissedAt && Date.now() - dismissedAt < this.DISMISSED_ENTRY_TTL_MS) {
            if (next.shouldStore !== false) {
              this.modalStateService.storeNotificationInLocalStorage(next.notification);
            }
            this.dialogMutex.release('way-purchase-modal');
            this.modalStateService.flushDeferredNotifications();
          } else {
            // Way still owns the mutex — do not flush deferred NotificationModal opens yet.
            this.modalStateSubject.next({
              isOpen: true,
              notification: next.notification,
              shouldStore: next.shouldStore,
            });
          }
        } else {
          this.dialogMutex.release('way-purchase-modal');
          this.modalStateService.flushDeferredNotifications();
        }
      } else {
        this.dialogMutex.release('way-purchase-modal');
        this.modalStateService.flushDeferredNotifications();
      }

      this.closingTimeout = null;
    }, 350);
  }

  clearNotificationQueue(): void {
    this.notificationQueue = [];
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }
    this.isClosing = false;
  }

  private openReplacing(
    notification: NotificationData,
    shouldStore: boolean,
    clearDismissSuppression: boolean
  ): void {
    if (clearDismissSuppression) {
      const entryId = this.getLprEntryId(notification);
      if (entryId) {
        this.dismissedEntryKeys.delete(`entry:${entryId}`);
      }
    }
    this.clearNotificationQueue();
    this.isClosing = false;
    this.modalStateSubject.next({
      isOpen: true,
      notification,
      shouldStore,
    });
  }

  private openRespectingState(notification: NotificationData, shouldStore: boolean): boolean {
    this.pruneDismissedEntryKeys();
    const currentState = this.modalStateSubject.value;

    const entryId = this.getLprEntryId(notification);
    if (entryId) {
      const dismissedAt = this.dismissedEntryKeys.get(`entry:${entryId}`);
      if (dismissedAt && Date.now() - dismissedAt < this.DISMISSED_ENTRY_TTL_MS) {
        this.logger.debug(`Suppressing duplicate Way purchase modal for entry:${entryId}`);
        if (shouldStore !== false) {
          this.modalStateService.storeNotificationInLocalStorage(notification);
        }
        return false;
      }
    }

    if (this.isClosing) {
      this.notificationQueue.push({ notification, shouldStore });
      return true;
    }

    if (currentState.isOpen) {
      if (shouldStore !== false) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      return true;
    }

    this.modalStateSubject.next({
      isOpen: true,
      notification,
      shouldStore,
    });
    return true;
  }

  private getLprEntryId(notification: NotificationData | null): string {
    if (!notification?.data) {
      return '';
    }
    const d = notification.data as Record<string, any>;
    return String(d['lpr_entry_id'] ?? d['lprEntryId'] ?? d['id'] ?? '').trim();
  }

  /** Drop expired suppression keys so the map stays bounded over long sessions. */
  private pruneDismissedEntryKeys(now = Date.now()): void {
    for (const [key, dismissedAt] of this.dismissedEntryKeys) {
      if (now - dismissedAt >= this.DISMISSED_ENTRY_TTL_MS) {
        this.dismissedEntryKeys.delete(key);
      }
    }
  }
}
